import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { Smartphone, Monitor, Apple, Chrome, Share, PlusSquare, MoreVertical, Download } from "lucide-react";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { BrutalButton } from "@/components/ui/BrutalButton";
import { BrutalCard } from "@/components/ui/BrutalCard";
import { SEO } from "@/components/SEO";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

const Install = () => {
  const { t } = useTranslation();
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Mark as visited so PWA banner doesn't show again
    localStorage.setItem("pwa-banner-dismissed", "true");
    
    // Check if already installed (standalone mode)
    const standalone = window.matchMedia("(display-mode: standalone)").matches || 
                       (window.navigator as any).standalone === true;
    setIsStandalone(standalone);

    // Detect platform
    const userAgent = navigator.userAgent.toLowerCase();
    const isIOSDevice = /iphone|ipad|ipod/.test(userAgent) && !(window as any).MSStream;
    const isAndroidDevice = /android/.test(userAgent);
    
    setIsIOS(isIOSDevice);
    setIsAndroid(isAndroidDevice);
    setIsDesktop(!isIOSDevice && !isAndroidDevice);

    // Listen for install prompt
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setIsInstallable(true);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;
    
    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === "accepted") {
      setDeferredPrompt(null);
      setIsInstallable(false);
    }
  };

  if (isStandalone) {
    return (
      <>
        <SEO 
          title={t("install.seoTitle")}
          description={t("install.seoDescription")}
        />
        <Header />
        <main className="min-h-screen bg-background pt-24 pb-16">
          <div className="container mx-auto px-4 max-w-2xl">
            <BrutalCard className="p-8 text-center">
              <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <Download className="w-8 h-8 text-primary" />
              </div>
              <h1 className="text-2xl font-bold mb-4">{t("install.alreadyInstalled")}</h1>
              <p className="text-muted-foreground">{t("install.alreadyInstalledDesc")}</p>
            </BrutalCard>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <SEO 
        title={t("install.seoTitle")}
        description={t("install.seoDescription")}
      />
      <Header />
      <main className="min-h-screen bg-background pt-24 pb-16">
        <div className="container mx-auto px-4 max-w-3xl">
          {/* Hero */}
          <div className="text-center mb-12">
            <h1 className="text-3xl md:text-4xl font-bold mb-4">{t("install.title")}</h1>
            <p className="text-lg text-muted-foreground max-w-xl mx-auto">
              {t("install.subtitle")}
            </p>
          </div>

          {/* Benefits */}
          <div className="grid md:grid-cols-3 gap-4 mb-12">
            <BrutalCard className="p-6 text-center">
              <Smartphone className="w-10 h-10 text-primary mx-auto mb-3" />
              <h3 className="font-semibold mb-2">{t("install.benefit1Title")}</h3>
              <p className="text-sm text-muted-foreground">{t("install.benefit1Desc")}</p>
            </BrutalCard>
            <BrutalCard className="p-6 text-center">
              <Download className="w-10 h-10 text-primary mx-auto mb-3" />
              <h3 className="font-semibold mb-2">{t("install.benefit2Title")}</h3>
              <p className="text-sm text-muted-foreground">{t("install.benefit2Desc")}</p>
            </BrutalCard>
            <BrutalCard className="p-6 text-center">
              <Monitor className="w-10 h-10 text-primary mx-auto mb-3" />
              <h3 className="font-semibold mb-2">{t("install.benefit3Title")}</h3>
              <p className="text-sm text-muted-foreground">{t("install.benefit3Desc")}</p>
            </BrutalCard>
          </div>

          {/* Install Button (Chrome/Edge on Android/Desktop) */}
          {isInstallable && (
            <div className="mb-12 text-center">
              <BrutalButton 
                size="lg" 
                onClick={handleInstallClick}
                className="gap-2"
              >
                <Download className="w-5 h-5" />
                {t("install.installNow")}
              </BrutalButton>
            </div>
          )}

          {/* Platform-specific instructions */}
          <div className="space-y-6">
            {/* iOS Instructions */}
            {(isIOS || isDesktop) && (
              <BrutalCard className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Apple className="w-6 h-6 text-foreground" />
                  <h2 className="text-xl font-bold">{t("install.iosTitle")}</h2>
                </div>
                <ol className="space-y-4 text-muted-foreground">
                  <li className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-bold">1</span>
                    <span className="flex items-center gap-2">
                      {t("install.iosStep1")} <Share className="w-4 h-4 inline text-foreground" />
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-bold">2</span>
                    <span className="flex items-center gap-2">
                      {t("install.iosStep2")} <PlusSquare className="w-4 h-4 inline text-foreground" />
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-bold">3</span>
                    <span>{t("install.iosStep3")}</span>
                  </li>
                </ol>
              </BrutalCard>
            )}

            {/* Android Instructions */}
            {(isAndroid || isDesktop) && (
              <BrutalCard className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Chrome className="w-6 h-6 text-foreground" />
                  <h2 className="text-xl font-bold">{t("install.androidTitle")}</h2>
                </div>
                <ol className="space-y-4 text-muted-foreground">
                  <li className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-bold">1</span>
                    <span className="flex items-center gap-2">
                      {t("install.androidStep1")} <MoreVertical className="w-4 h-4 inline text-foreground" />
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-bold">2</span>
                    <span>{t("install.androidStep2")}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-bold">3</span>
                    <span>{t("install.androidStep3")}</span>
                  </li>
                </ol>
              </BrutalCard>
            )}

            {/* Desktop Instructions */}
            {isDesktop && (
              <BrutalCard className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Monitor className="w-6 h-6 text-foreground" />
                  <h2 className="text-xl font-bold">{t("install.desktopTitle")}</h2>
                </div>
                <ol className="space-y-4 text-muted-foreground">
                  <li className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-bold">1</span>
                    <span>{t("install.desktopStep1")}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-bold">2</span>
                    <span>{t("install.desktopStep2")}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-bold">3</span>
                    <span>{t("install.desktopStep3")}</span>
                  </li>
                </ol>
              </BrutalCard>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
};

export default Install;
